import { FC, useCallback, useEffect, useRef, useState } from 'react'

import { ArrowRight } from '@Static/icons/AccountOrdersPageIcons'
import cn from 'clsx'

import s from './styles.module.scss'

type PriceAlertCarouselProps = {
  addPercentToAlert: (percent: number) => void
}

const defaultPercent = [-10, -5, -4, -3, -2, -1, -0.5, 0, 0.5, 1, 2, 3, 4, 5, 10]

export const PriceAlertCarousel: FC<PriceAlertCarouselProps> = ({ addPercentToAlert }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const scrollDirectionRef = useRef<0 | 1 | -1>(0)

  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  const checkScrollPosition = useCallback(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return

    const maxScroll = content.scrollWidth - container.clientWidth

    setShowLeftArrow(container.scrollLeft > 0)
    setShowRightArrow(maxScroll > 0 && container.scrollLeft < maxScroll - 1)
  }, [])

  const performScroll = useCallback(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content || scrollDirectionRef.current === 0) return

    const maxScroll = content.scrollWidth - container.clientWidth
    if (maxScroll <= 0) return

    const speed = 4
    const delta = scrollDirectionRef.current * speed

    container.scrollLeft = Math.min(Math.max(container.scrollLeft + delta, 0), maxScroll)
    rafRef.current = requestAnimationFrame(performScroll)
  }, [])

  const startScrolling = useCallback(
    (direction: 'left' | 'right') => {
      if (scrollDirectionRef.current !== 0) return

      scrollDirectionRef.current = direction === 'left' ? -1 : 1
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(performScroll)
      }
    },
    [performScroll],
  )

  const stopScrolling = useCallback(() => {
    scrollDirectionRef.current = 0
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const handlePointerDown = useCallback(
    (direction: 'left' | 'right', e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()

      startScrolling(direction)

      const handlePointerUp = (upEvent: PointerEvent) => {
        if (upEvent.pointerType === e.pointerType) {
          stopScrolling()
          document.removeEventListener('pointerup', handlePointerUp)
          document.removeEventListener('pointercancel', handlePointerUp)
        }
      }

      document.addEventListener('pointerup', handlePointerUp)
      document.addEventListener('pointercancel', handlePointerUp)
    },
    [startScrolling, stopScrolling],
  )

  useEffect(() => {
    return () => {
      stopScrolling()
    }
  }, [stopScrolling])

  useEffect(() => {
    const container = containerRef.current
    const content = contentRef.current
    if (!container || !content) return

    const onScroll = () => checkScrollPosition()
    container.addEventListener('scroll', onScroll)

    const resizeObserver = new ResizeObserver(() => checkScrollPosition())
    resizeObserver.observe(container)
    resizeObserver.observe(content)

    checkScrollPosition()

    return () => {
      container.removeEventListener('scroll', onScroll)
      resizeObserver.disconnect()
    }
  }, [checkScrollPosition])

  return (
    <div className={s.wrapper}>
      {showLeftArrow && (
        <div className={cn(s.arrowButton, s.arrowButtonLeft)} onPointerDown={(e) => handlePointerDown('left', e)}>
          <ArrowRight className={cn(s.arrowIcon, s.arrowLeft)} />
        </div>
      )}

      <div
        ref={containerRef}
        className={cn(
          s.scrollContainer,
          showLeftArrow && s.scrollContainerLeftShadow,
          showRightArrow && s.scrollContainerRightShadow,
        )}
      >
        <div ref={contentRef} className={s.content}>
          {defaultPercent.map((item, index) => (
            <div
              onClick={() => addPercentToAlert(item)}
              key={index}
              className={cn(s.item, item > 0 && s.itemPositive, item < 0 && s.itemNegative)}
            >
              {`${item > 0 ? '+' : ''}${item}%`}
            </div>
          ))}
        </div>
      </div>

      {showRightArrow && (
        <div className={cn(s.arrowButton, s.arrowButtonRight)} onPointerDown={(e) => handlePointerDown('right', e)}>
          <ArrowRight className={s.arrowIcon} />
        </div>
      )}
    </div>
  )
}
